import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useDebounce } from "../utils/hooks/useDebounce";
import { exportCustomers } from "../services";
import {
  CustomerBilltype,
  CustomerEtaxChannel,
  CustomerSortBy,
  CustomerStatus,
  SortOrder,
} from "../types/customer.type";
import ExportButton from "../components/ExportButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import CustomerTable from "../components/Customer/Table";
import CustomerFilter from "../components/Customer/Filter";
import CustomerDrawer from "../components/Customer/CustomerDrawer";
import { CUSTOMER_BRANCH_PARAM } from "../components/Customer/data";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";

export default function CustomerPage() {
  // Marketing only binds shops to pricebooks (from the shop's page), so it gets no create
  // or edit drawer here.
  const canEditCustomer = featureAccess.customerEdit(useUserRole());
  const [searchName, setSearchName] = useState<string>("");
  const [status, setStatus] = useState<string>("all");
  const [billType, setBillType] = useState<string>("all");
  const [etaxChannel, setEtaxChannel] = useState<string>("all");
  const [customerCode, setCustomerCode] = useState<string>("");
  const [routeCode, setRouteCode] = useState<string>("all");
  const [searchParams] = useSearchParams();
  // A link may open the list on one branch, e.g. a pricebook draft's 00-HO test shops.
  const [branch, setBranch] = useState<string>(
    searchParams.get(CUSTOMER_BRANCH_PARAM) ?? "all",
  );
  const [sortBy, setSortBy] = useState<string>("created");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [customerId, setCustomerId] = useState<string>("");
  const [triggerFetch, setTriggerFetch] = useState<number>(0);

  const debouncedSearch = useDebounce(searchName, 500);
  const debouncedCustomerCode = useDebounce(customerCode, 500);

  // Same filters the table sends (its "all" sentinel dropped), minus the paging.
  const cleanParam = (value: string) =>
    value && value !== "all" ? value : undefined;

  const handleExport = () =>
    exportCustomers({
      name: debouncedSearch || undefined,
      customer_code: debouncedCustomerCode || undefined,
      route_code: cleanParam(routeCode),
      branch_uuid: cleanParam(branch),
      status: cleanParam(status) as CustomerStatus | undefined,
      bill_type: cleanParam(billType) as CustomerBilltype | undefined,
      etax_channel: cleanParam(etaxChannel) as CustomerEtaxChannel | undefined,
      sort_by: (sortBy || undefined) as CustomerSortBy | undefined,
      sort_order: (sortOrder || undefined) as SortOrder | undefined,
    });

  const handleOpenCreateDrawer = () => {
    setCustomerId("");
    setOpenDrawer(true);
  };

  const handleOpenEditDrawer = (id: string) => {
    setCustomerId(id);
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setCustomerId("");
  };

  const handleSaved = () => setTriggerFetch((prev) => prev + 1);

  return (
    <>
      <Box sx={{ padding: "8px 0" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6">ลูกค้า</Typography>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
              flexWrap="wrap"
            >
              <ExportButton
                request={handleExport}
                fallbackFilename={`customers_${dayjs().format("YYYYMMDD")}.xlsx`}
              />
              {canEditCustomer && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateDrawer}
                >
                  สร้างลูกค้าใหม่
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        <Paper
          sx={{
            padding: "24px 16px 8px",
            mb: 2,
          }}
        >
          <CustomerFilter
            searchName={searchName}
            setSearchName={setSearchName}
            status={status}
            setStatus={setStatus}
            billType={billType}
            setBillType={setBillType}
            etaxChannel={etaxChannel}
            setEtaxChannel={setEtaxChannel}
            customerCode={customerCode}
            setCustomerCode={setCustomerCode}
            routeCode={routeCode}
            setRouteCode={setRouteCode}
            branch={branch}
            setBranch={setBranch}
          />
        </Paper>
        <CustomerTable
          searchName={debouncedSearch}
          customerCode={debouncedCustomerCode}
          status={status}
          billType={billType}
          etaxChannel={etaxChannel}
          routeCode={routeCode}
          branch={branch}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          triggerFetch={triggerFetch}
          onOpenEditDrawer={
            canEditCustomer ? handleOpenEditDrawer : undefined
          }
        />
      </Box>
      {canEditCustomer && (
        <CustomerDrawer
          customerId={customerId}
          open={openDrawer}
          onClose={handleCloseDrawer}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
